import axios from 'axios';
import { publicIssueMockApi } from '../mocks/publicIssueMock';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const IS_MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

const api = axios.create({
  baseURL: `${API_BASE_URL}/v1/public`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const unwrap = (response) => response?.data?.data;
const isNetworkError = (error) => !error?.response;

const isMockEnabled = () => {
  if (IS_MOCK_MODE) {
    return true;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('mock') === '0') {
    return false;
  }
  if (params.get('mock') === '1') {
    return true;
  }
  return false;
};

const withFallback = async (realCall, mockCall) => {
  if (isMockEnabled()) {
    return mockCall();
  }
  try {
    return await realCall();
  } catch (error) {
    if (isNetworkError(error)) {
      return mockCall();
    }
    throw error;
  }
};

/** 실제 발급 토큰(URL 등으로 전달된 값)이면 서버 호출, mock 전용 토큰이면 기존 mock/fallback */
const isRealIssueToken = (token) => token && token !== 'mock-issue-token';
/** 서버에서 내려준 UUID 세션이면 이후 API도 서버 호출 (mock 세션 ID는 sess_mock_...) */
const isRealSessionId = (sessionId) => sessionId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId);

export const publicIssueApi = {
  startSession: async (issueToken) => {
    if (isRealIssueToken(issueToken)) {
      try {
        const response = await api.post('/issue-sessions/start', { issue_token: issueToken });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.startSession(issueToken);
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/issue-sessions/start', { issue_token: issueToken });
        return unwrap(response);
      },
      async () => publicIssueMockApi.startSession(issueToken),
    );
  },

  getSession: async (sessionId) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.get(`/issue-sessions/${sessionId}`);
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.getSession(sessionId);
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.get(`/issue-sessions/${sessionId}`);
        return unwrap(response);
      },
      async () => publicIssueMockApi.getSession(sessionId),
    );
  },

  startPass: async (sessionId) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.post('/pass/start', { session_id: sessionId });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.startPass(sessionId);
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/pass/start', { session_id: sessionId });
        return unwrap(response);
      },
      async () => publicIssueMockApi.startPass(sessionId),
    );
  },

  completePass: async ({ sessionId, name, phone, passResult = 'success' }) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.post('/pass/callback', {
          session_id: sessionId,
          pass_result: passResult,
          name,
          phone,
        });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.completePass({ sessionId, name, phone, passResult });
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/pass/callback', {
          session_id: sessionId,
          pass_result: passResult,
          name,
          phone,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.completePass({ sessionId, name, phone, passResult }),
    );
  },

  requestAccountVerification: async ({ sessionId, bankCode, accountNumber, accountHolderName }) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.post('/account-verifications/request', {
          session_id: sessionId,
          bank_cd: bankCode,
          account_number: accountNumber,
          account_holder_name: accountHolderName,
        });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) {
          return publicIssueMockApi.requestAccountVerification({
            sessionId,
            bankCode,
            accountNumber,
            accountHolderName,
          });
        }
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/account-verifications/request', {
          session_id: sessionId,
          bank_cd: bankCode,
          account_number: accountNumber,
          account_holder_name: accountHolderName,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.requestAccountVerification({
        sessionId,
        bankCode,
        accountNumber,
        accountHolderName,
      }),
    );
  },

  confirmAccountVerification: async ({ sessionId, verificationCode }) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.post('/account-verifications/confirm', {
          session_id: sessionId,
          verification_code: verificationCode,
        });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.confirmAccountVerification({ sessionId, verificationCode });
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/account-verifications/confirm', {
          session_id: sessionId,
          verification_code: verificationCode,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.confirmAccountVerification({ sessionId, verificationCode }),
    );
  },

  issueVirtualAccount: async ({ sessionId, depositorName, amount = 0, bankCode }) => {
    if (isRealSessionId(sessionId)) {
      try {
        const response = await api.post('/virtual-accounts', {
          session_id: sessionId,
          depositorName,
          amount,
          bank_cd: bankCode,
        });
        return unwrap(response);
      } catch (error) {
        if (isNetworkError(error)) return publicIssueMockApi.issueVirtualAccount({ sessionId, depositorName, amount, bankCode });
        throw error;
      }
    }
    return withFallback(
      async () => {
        const response = await api.post('/virtual-accounts', {
          session_id: sessionId,
          depositorName,
          amount,
          bank_cd: bankCode,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.issueVirtualAccount({ sessionId, depositorName, amount, bankCode }),
    );
  },
};
